import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, Typography, Button, useTheme } from '@mui/joy';

import HeaderFlow from '../../assets/images/HeaderFlow.svg?react';
import LinkSvg from '../../assets/images/Link.svg?react';
import Selects from '../../assets/images/Selects.svg?react';
import Share from '../../assets/images/Share.svg?react';
import SiflyWhiteLogo from '../../assets/images/WhiteSiflyLogo.svg?react';
import { FitMode } from '../VideoScaler';
import VideoScreenFitDropdown from './VideoScreenFitDropdown';

interface CrewViewHeaderProps {
  sessionName: string;
  link: string;
  fitMode?: FitMode;
  onFitModeChange?: (mode: FitMode) => void;
}

const CrewViewHeader = ({ sessionName, link, fitMode, onFitModeChange }: CrewViewHeaderProps) => {
  const navigate = useNavigate();
  const [mobileDropdownState, setMobileDropdownState] = useState<'main' | 'share' | null>(null);
  const [isFollowDropdownOpen, setIsFollowDropdownOpen] = useState(false);
  const followDropdownRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const { t } = useTranslation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking on the Select dropdown listbox
      const isSelectListbox = (target as Element).closest('[role="listbox"]');
      if (isSelectListbox) {
        return;
      }

      if (followDropdownRef.current && !followDropdownRef.current.contains(target)) {
        setIsFollowDropdownOpen(false);
      }
    };

    if (isFollowDropdownOpen) {
      // Use a slight delay to allow Select dropdown to open first
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isFollowDropdownOpen]);

  return (
    <Box className='w-full relative z-20'>
      <Box className='hidden sm:flex flex-row h-[40px] gap-2 max-w-screen'>
        <Box className='flex flex-row items-center justify-center mx-4 cursor-pointer' onClick={() => navigate('/')}>
          <SiflyWhiteLogo fill={theme.palette.common.white} />
        </Box>
        <Box
          className='flex flex-row items-center px-2 border-l border-r relative'
          sx={{ borderColor: 'neutral.outlinedDisabledColor' }}
          ref={followDropdownRef}
        >
          <Box
            className='flex flex-row items-center gap-3 cursor-pointer'
            onClick={(e) => {
              e.stopPropagation();
              setIsFollowDropdownOpen(!isFollowDropdownOpen);
            }}
            sx={{ pointerEvents: 'auto' }}
          >
            <HeaderFlow className='w-4 h-4' fill='var(--joy-palette-text-primary)' />
            <Typography level='body-md' sx={{ color: 'text.primary' }}>
              Follow
            </Typography>
            <Selects className='w-4 h-4' fill='var(--joy-palette-text-primary)' />
          </Box>
          {isFollowDropdownOpen && fitMode && onFitModeChange && (
            <Box className='absolute top-full left-0 mt-2'>
              <VideoScreenFitDropdown value={fitMode} onChange={onFitModeChange} />
            </Box>
          )}
        </Box>
      </Box>
      <Box className='sm:hidden'>
        <Box
          className='flex flex-col items-start justify-start h-fit min-h-[40px] px-4 relative z-50'
          onClick={() => setMobileDropdownState(mobileDropdownState === null ? 'main' : null)}
        >
          <Box className='flex flex-row items-center mt-4 mx-2 h-full'>
            <SiflyWhiteLogo fill='#FFF' onClick={() => navigate('/')} />
          </Box>
          {mobileDropdownState === 'main' && (
            <Box className='flex flex-col mt-3 items-center px-2'>
              <Box
                className='flex flex-row items-center gap-3'
                onClick={(e) => {
                  e.stopPropagation();
                  setMobileDropdownState('share');
                }}
              >
                <Share className='w-4 h-4' fill='var(--joy-palette-text-tertiary)' />
                <Typography level='body-md' sx={{ color: 'text.primary' }}>
                  {t('crew_view.header.share')}
                </Typography>
              </Box>
            </Box>
          )}
          {mobileDropdownState === 'share' && (
            <Box className='flex flex-col mt-3 items-center px-2'>
              <Typography level='body-md' sx={{ color: 'text.primary' }}>
                Share {sessionName}
              </Typography>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(link);
                }}
                className='flex items-center gap-2 px-1 py-2'
                sx={{
                  bgcolor: 'background.level1',
                  '&:hover': {
                    bgcolor: 'background.level2',
                    border: 1
                  }
                }}
              >
                <LinkSvg />
                <Typography level='body-md' sx={{ color: 'text.primary' }}>
                  {t('crew_view.settings.copy_link')}
                </Typography>
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CrewViewHeader;
